import { z } from "zod";
import type {
  AgentThinkingTraceStep,
  AgentVisibleLoopStep,
} from "@/types/createAgent";

export const MAX_VISIBLE_THINKING_STEPS = 32;

export const thinkingStepSchema = z.object({
  id: z.string().min(1).max(120),
  kind: z.enum(["context", "thought", "action", "observation", "decision"]),
  title: z.string().min(1).max(180),
  summary: z.string().max(600),
  source: z.enum(["llm", "tool", "system"]).optional(),
  callId: z.string().max(120).optional(),
  phase: z.enum(["request", "response", "error"]).optional(),
  outcome: z.enum(["ok", "skipped"]).optional(),
  toolName: z.string().max(120).optional(),
  iteration: z.number().int().nonnegative().max(100).optional(),
  state: z.enum(["running", "complete"]),
  timestamp: z.number().int().nonnegative().finite(),
});
export const thinkingTraceSchema = z
  .array(thinkingStepSchema)
  .max(MAX_VISIBLE_THINKING_STEPS);

function shouldShowStep(
  step: Pick<AgentVisibleLoopStep, "source" | "kind" | "outcome" | "phase">
): boolean {
  return (
    (step.source === "llm" && step.phase !== "error") ||
    (step.source === "tool" &&
      step.kind === "observation" &&
      step.outcome === "ok")
  );
}

/** Persist bounded planner activity and confirmed tool results on replies. */
export function normalizeCompletedThinkingTrace(
  value: unknown
): AgentThinkingTraceStep[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const steps = value.slice(0, MAX_VISIBLE_THINKING_STEPS).flatMap(item => {
    const parsed = thinkingStepSchema.safeParse(item);
    return parsed.success && shouldShowStep(parsed.data)
      ? [{ ...parsed.data, state: "complete" as const }]
      : [];
  });
  return steps.length > 0 ? steps : undefined;
}

/** Match the live disclosure's call-id updates while collecting server SSE. */
export function appendVisibleThinkingStep(
  steps: AgentThinkingTraceStep[],
  status: string,
  toolName: string | undefined,
  loop: AgentVisibleLoopStep | undefined,
  timestamp: number
): AgentThinkingTraceStep[] {
  if (!loop || !shouldShowStep(loop)) return steps;
  const summary = (loop.summary.trim() || status.trim()).slice(0, 600);
  const title = loop.title.trim().slice(0, 180);
  if (!title || !summary) return steps;
  const callId = loop.callId?.trim().slice(0, 120) || undefined;
  const phase = loop.phase;
  const nextState = phase === "request" ? "running" : "complete";
  const latest = steps[steps.length - 1];
  if (
    latest?.title === title &&
    latest.summary === summary &&
    latest.toolName === toolName
  ) {
    return steps;
  }
  const step: AgentThinkingTraceStep = {
    id: `thinking-${timestamp}-${steps.length}`,
    kind: loop.kind,
    title,
    summary,
    source: loop.source,
    ...(callId && { callId }),
    ...(phase && { phase }),
    ...(loop.outcome && { outcome: loop.outcome }),
    ...(toolName && { toolName: toolName.slice(0, 120) }),
    ...(loop.iteration !== undefined && { iteration: loop.iteration }),
    state: nextState,
    timestamp,
  };
  if (callId) {
    const index = steps.findIndex(candidate => candidate.callId === callId);
    if (index >= 0) {
      return steps.map((candidate, candidateIndex) =>
        candidateIndex === index
          ? { ...step, id: candidate.id, timestamp: candidate.timestamp }
          : { ...candidate, state: "complete" as const }
      );
    }
  }
  return [
    ...steps.map(candidate => ({ ...candidate, state: "complete" as const })),
    step,
  ].slice(-MAX_VISIBLE_THINKING_STEPS);
}
